import { UserType } from "@/constants";
import { request } from "@umijs/max";
import { ResourceType } from "./order";
import { SeatTermTypeEnum } from "./resource";
import { IRole } from "./organization";
import { ResourceItem } from "@packages/components";

export interface ITenant {
  contactNumber: string;
  linkMan: string;
  tenantId: string;
  tenantName: string;
}

export interface IUser {
  account: string;
  clientId: string;
  deptId: string;
  detail: Record<string, unknown>;
  nickName: string;
  oauthId: string;
  postId: string;
  roleId: string;
  roleName: string;
  tenantId: string;
  userId: string;
  userName: string;
  avatar: string;
  phone: string;
  email: string;
}

/** 菜单类型 */
export enum MenuType {
  MENU = "MENU",
}

/** 用户的资源类型 */
export enum UserResourceType {
  PAGE = "PAGE",
}

/** 菜单项 */
export interface IMenuItem {
  code: string;
  parentCode: string;
  /** 菜单显示的名字，目前只有中文 */
  label: string;
  /** 对应route的name */
  name: string;
  /** 菜单的路径 */
  path: string;
  /** 菜单类型 */
  type: MenuType;
  /** 菜单的排序 */
  order: number;
  /** 菜单的子菜单 */
  children: IMenuItem[];
}

export interface IResource {
  code: string;
  name: string;
  label: string;
  path: string;
  menuCode: string;
  type: UserResourceType;
}

export interface IUserInfo {
  enableEmail: boolean;
  enablePhone: boolean;
  // role: Role; 弃用
  tenant: ITenant;
  user: IUser;
  menuList: IMenuItem[];
  resourceList: IResource[];
  menus: ResourceItem[];
  pagePerms: {
    path: string;
    elementPerms: string[];
  }[];
  userType: UserType;
  /** 角色信息 */
  roleInfo: IRole;
}

export function getUserInfoService() {
  return request<API.BaseResponse<IUserInfo>>(
    "/api/damselfish-central-backend/manage/info"
  );
}

export function logoutService() {
  return request<API.BaseResponse>("/api/blade-auth/oauth/logout");
}

export interface IUpdateUserDto {
  nickName: string;
  email: string;
  enableEmail: boolean;
  enablePhone: boolean;
  phone: string;
}
export function updateUserInfoService(data: Partial<IUpdateUserDto>) {
  return request<API.BaseResponse>(
    "/api/damselfish-central-backend/manage/updateAccountInfo",
    {
      method: "PUT",
      data,
    }
  );
}

export enum SeatStatus {
  ENABLE = "ENABLE",
  DISABLE = "DISABLE",
}
export interface IGetSeatListDto {
  pageNum: number;
  pageSize: number;
  resourceType: ResourceType;
  robotStatus: SeatStatus;
  sort: "ASC" | "DESC";
  termType: SeatTermTypeEnum;
}
export interface ISeatInfo {
  detailStatus: SeatStatus;
  endTime: string;
  name: string;
  resourceId: number;
}
export function getSeatListService(data: Partial<IGetSeatListDto>) {
  return request<API.BaseListResponse<ISeatInfo>>(
    "/api/damselfish-fee-center/userSearch/seatPage",
    {
      method: "POST",
      data,
    }
  );
}

export function getUserPhoneAndEmailSerivce() {
  return request<API.BaseResponse<{ phone: string; email: string }>>(
    "/api/damselfish-fee-center/userSearch/account"
  );
}

export const getWxUserOptionsService = (params: {
  tenantId: string;
  isOnline?: boolean;
}) => {
  return request<
    API.BaseResponse<{ userId: string; name: string; avatar: string }[]>
  >("/api/damselfish-chat-bot/flow/inner-user", {
    params,
  });
};

/** 发送验证码 */
export function sendCaptchaService(account: string) {
  return request<API.BaseResponse<null>>(
    "/api/damselfish-central-backend/manage/captcha",
    {
      method: "POST",
      data: { account: account?.trim() },
    }
  );
}
/** 修改自己的密码 */
export function updatePasswordService(data: {
  captcha: string;
  password: string;
}) {
  return request<API.BaseResponse>(
    "/api/damselfish-central-backend/manage/updatePassword",
    { method: "PUT", data }
  );
}
/** 修改ram用户密码 */
export function updateRAMUserPasswordService(data: {
  userId: string;
  password: string;
}) {
  return request<API.BaseResponse>(
    "/api/damselfish-central-backend/manage/updateRamPassword",
    { method: "PUT", data }
  );
}

export enum BenefitType {
  BOOLEAN_TYPE = "BOOLEAN_TYPE",
  LONG_TYPE = "LONG_TYPE",
}
export enum BenefitName {
  /** 机器人数量 */
  AGENT_LIMIT = "AGENT_LIMIT",
  /** 问答机器人 */
  QA_AGENT = "QA_AGENT",
  /** 接待机器人 */
  RECEPTION_AGENT = "RECEPTION_AGENT",
  /** 智能标签机器人 */
  SMART_TAG_AGENT = "SMART_TAG_AGENT",
  /** 跟进机器人 */
  FOLLOW_UP_AGENT = "FOLLOW_UP_AGENT",
  /** 销售机器人 */
  SELL_AGENT = "SELL_AGENT",
  /** 客服机器人 */
  CUSTOMER_SERVICE_AGENT = "CUSTOMER_SERVICE_AGENT",
  /** 销售总结机器人 */
  SELL_SUMMARY_AGENT = "SELL_SUMMARY_AGENT",
  /** 支持多账号管理 */
  SUPPORT_AGG_MANAGEMENT = "SUPPORT_AGG_MANAGEMENT",
  /** 标签和群 */
  TAG_AND_GROUP = "TAG_AND_GROUP",
  /** 支持http节点 */
  SUPPORT_HTTP_NODE = "SUPPORT_HTTP_NODE",
  /** 同步标签 */
  SYNC_TAG = "SYNC_TAG",
  /** 包含机器人节点 */
  CONTAIN_ROBOT_NODE = "CONTAIN_ROBOT_NODE",
  /** 每分钟限制 */
  LIMIT_PER_TIME = "LIMIT_PER_TIME",
  /** 每月限制 */
  LIMIT_PER_MONTH = "LIMIT_PER_MONTH",
  /** 销售总结机器人每月限制 */
  SOP_LIMIT_PER_MONTH = "SOP_LIMIT_PER_MONTH",
  /** 支持mcp节点 */
  SUPPORT_MCP_NODE = "SUPPORT_MCP_NODE",
  /** 支持rpa节点 */
  SUPPORT_RPA_NODE = "SUPPORT_RPA_NODE",
  /** api调用限制 */
  API_CALL_LIMIT = "API_CALL_LIMIT",
  /** 流程子账号限制 */
  FLOW_SUB_ACCOUNT_LIMIT = "FLOW_SUB_ACCOUNT_LIMIT",
  /** 每月限制 */
  POWER_PER_MONTH = "POWER_PER_MONTH",
  /** 销售总结机器人包含机器人节点 */
  SOP_HAS_ROBOT_NODE = "SOP_HAS_ROBOT_NODE",
}
export interface IBenefit {
  resourceId: number;
  benefitName: BenefitName;
  benefitType: BenefitType;
  nums: number;
  residualNums: number;
}

export function getMemberBenefitService() {
  return request<API.BaseResponse<IBenefit[]>>(
    "/api/damselfish-fee-center/tenantResourceStatistic/getMemberBenefit"
  );
}
